import { ArrowRight, Sparkles } from "lucide-react";
import { useBins } from "@/components/BinaryGrid";
import { DenoiseSection, ProductStrip } from "@/components/DenoiseSection";
import { PricingSection } from "@/components/PricingSection";
import { SkinSection } from "@/components/SkinSection";
import { TextureSection } from "@/components/TextureSection";
import { ReactorSection } from "@/components/ReactorSection";
import { FilmCompressionSection } from "@/components/FilmCompressionSection";
import { LaunchBanner } from "@/components/LaunchPromo";
import { SupportSection } from "@/components/SupportSection";
import { MINT } from "@/components/RecastSection";

const PREFIXES = ["denoise-", "skin-", "texture-", "reactor-", "rolloff-"];

const AiTeaser = () => (
  <a href="/ai" className="group card p-6 flex flex-col md:flex-row md:items-center gap-5 transition-colors hover:border-[#4ff2c2]" data-testid="ai-teaser">
    <Sparkles size={28} style={{ color: MINT }} className="flex-none" />
    <div className="flex-1">
      <div className="chip mb-2" style={{ color: MINT, borderColor: MINT }}>new · KolorKlinic AI</div>
      <h2 className="font-display uppercase text-2xl md:text-3xl leading-none">AI tools for Resolve: <span className="text-[#e69d27]">Gen Alpha</span> + <span style={{ color: MINT }}>Recast</span></h2>
      <p className="text-sm text-[#9ba3b4] mt-2 max-w-3xl">Character mattes for whole shots, and performance fixes without a reshoot (smile, blinks, gaze, head pose). Pay once, run on your own fal.ai key.</p>
    </div>
    <span className="flex items-center gap-1 font-semibold uppercase text-sm whitespace-nowrap" style={{ color: MINT }}>Explore KolorKlinic AI <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></span>
  </a>
);

export default function Plugin() {
  const { bins, dl, rebuild } = useBins(PREFIXES);

  return (
    <div className="space-y-10" data-testid="plugin-root">
      <LaunchBanner />
      {/* HERO */}
      <section id="top" className="scroll-mt-6 relative film-grain overflow-hidden hair-border bg-[#121418]" data-testid="hero">
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              "url('https://static.prod-images.emergentagent.com/jobs/403a8ff9-2cf1-4db1-8cc2-e84c88c07aaf/images/c5424a4f09ae48fb636301c9783b97547a687187fa096993304a5f70d9f4dca4.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "contrast(1.1) saturate(0.85)",
          }}
          data-testid="hero-still"
          aria-hidden
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,#0b0c0e 0%, rgba(11,12,14,0.78) 55%, rgba(11,12,14,0.45) 100%)" }} aria-hidden />
        <div className="relative px-10 py-16 max-w-[1100px]">
          <div className="chip mb-6"><span className="status-dot ok" />Resolve 19.1+ · Metal · CUDA · OpenCL · offline</div>
          <h1 className="font-display font-black uppercase text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            KolorKlinic
            <br />
            <span className="text-[#00e5ff]">Finishing tools</span>
          </h1>
          <p className="mt-4 font-display italic text-xl md:text-2xl text-[#f2f4f8]" data-testid="tagline">Made by colorists, for colorists.</p>
          <a href="#texture" className="mt-3 inline-flex items-baseline gap-3 font-display uppercase italic text-2xl md:text-3xl group" data-testid="hero-texture-catch">
            <span className="text-[#f2f4f8]">Digital in.</span>
            <span className="text-[#ff6a3d] transition-transform group-hover:translate-x-1">Film out.</span>
            <span className="text-xs not-italic font-mono tracking-widest text-[#9ba3b4]">· KolorKlinic Texture</span>
          </a>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-[#9ba3b4] leading-relaxed">
            GPU-native OpenFX plugins for DaVinci Resolve that run right inside Resolve's own GPU memory.
            Give digital the texture of film, retouch skin without the plastic look, build film colour looks, clean noise without losing texture, and stay in real time.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#texture" className="px-6 py-3 flex items-center gap-2 font-semibold uppercase bg-[#ff6a3d] text-black hover:bg-[#ff8a63] transition-colors" data-testid="hero-texture-btn">Try Texture free</a>
            <a href="#skin" className="px-6 py-3 flex items-center gap-2 font-semibold uppercase bg-[#ff7a9c] text-black hover:bg-[#ff9bb5] transition-colors" data-testid="hero-skin-btn">Try Skin free</a>
            <a href="#denoise" className="btn-ghost px-6 py-3 flex items-center gap-2" data-testid="hero-denoise-btn">Explore Denoise</a>
            <a href="#pricing" className="btn-ghost px-6 py-3 flex items-center gap-2" data-testid="hero-pricing-btn">Pricing</a>
          </div>
        </div>
      </section>

      <ProductStrip />

      <TextureSection bins={bins} dl={dl} rebuild={rebuild} />

      <ReactorSection bins={bins} dl={dl} rebuild={rebuild} />

      <SkinSection bins={bins} dl={dl} rebuild={rebuild} />

      <DenoiseSection bins={bins} dl={dl} rebuild={rebuild} />

      <FilmCompressionSection bins={bins} dl={dl} rebuild={rebuild} />

      <AiTeaser />

      <PricingSection />

      <SupportSection />
    </div>
  );
}
